import React, { createContext, useContext, useState, useEffect } from 'react';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  stock?: number; // Add stock property to enforce limits
}

export interface AppliedPromocode {
  id: string;
  code: string;
  type: 'percentage' | 'fixed_amount';
  value: number;
  discountAmount: number;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  appliedPromocode: AppliedPromocode | null;
  setAppliedPromocode: (promocode: AppliedPromocode | null) => void;
  promocodeValidationMessage: string;
  setPromocodeValidationMessage: (message: string) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    const savedItems = localStorage.getItem('cart');
    return savedItems ? JSON.parse(savedItems) : [];
  });
  const [appliedPromocode, setAppliedPromocode] = useState<AppliedPromocode | null>(() => {
    const savedPromocode = localStorage.getItem('appliedPromocode');
    return savedPromocode ? JSON.parse(savedPromocode) : null;
  });
  const [promocodeValidationMessage, setPromocodeValidationMessage] = useState<string>('');
  const [isRevalidatingPromocode, setIsRevalidatingPromocode] = useState(false);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (appliedPromocode) {
      localStorage.setItem('appliedPromocode', JSON.stringify(appliedPromocode));
    } else {
      localStorage.removeItem('appliedPromocode');
    }
  }, [appliedPromocode]);

  // Auto-revalidate promocode when cart contents change
  useEffect(() => {
    const revalidatePromocode = async () => {
      // Don't revalidate if no promocode is applied or already revalidating
      if (!appliedPromocode || isRevalidatingPromocode) {
        return;
      }

      // Calculate current cart subtotal
      const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      
      // If cart is empty, clear the promocode
      if (items.length === 0 || subtotal === 0) {
        setAppliedPromocode(null);
        setPromocodeValidationMessage('');
        return;
      }

      // Prevent multiple simultaneous revalidations
      setIsRevalidatingPromocode(true);

      try {
        // Import the service function dynamically to avoid circular dependency issues
        const { applyPromocodeToCart } = await import('../services/promocodes');
        
        // Re-apply the promocode to get updated discount
        const revalidatedPromocode = await applyPromocodeToCart(appliedPromocode.code, subtotal);
        
        // Update the promocode with new discount amount
        setAppliedPromocode(revalidatedPromocode);
        setPromocodeValidationMessage('');
        
        console.log('Promocode revalidated successfully:', revalidatedPromocode);
      } catch (error) {
        // Promocode is no longer valid, remove it and show message
        console.log('Promocode became invalid:', error);
        setAppliedPromocode(null);
        setPromocodeValidationMessage(
          error instanceof Error ? error.message : 'Sooduskood ei kehti enam'
        );
      } finally {
        setIsRevalidatingPromocode(false);
      }
    };

    // Debounce the revalidation to avoid excessive API calls during rapid cart changes
    const timeoutId = setTimeout(revalidatePromocode, 1); // Timeout for updating promocode in the cart
    
    return () => clearTimeout(timeoutId);
  }, [items, appliedPromocode?.code, isRevalidatingPromocode]);

  const addItem = (newItem: CartItem) => {
    // Make sure the item name is never undefined
    const itemToAdd = {
      ...newItem,
      name: newItem.name || "Toode"
    };
    
    // Ensure we don't add more than stock
    const safeQuantity = Math.min(
      itemToAdd.quantity,
      itemToAdd.stock !== undefined ? itemToAdd.stock : Infinity
    );
    
    if (safeQuantity <= 0) return; // Don't add out-of-stock items

    // Clear any previous validation message when adding new items
    setPromocodeValidationMessage('');
    
    setItems(currentItems => {
      const existingItem = currentItems.find(item => item.id === itemToAdd.id);
      if (existingItem) {
        // Calculate how many more can be added without exceeding stock
        const currentQuantity = existingItem.quantity;
        const maxAddable = itemToAdd.stock !== undefined 
          ? Math.max(0, itemToAdd.stock - currentQuantity)
          : itemToAdd.quantity;
          
        const quantityToAdd = Math.min(itemToAdd.quantity, maxAddable);
        
        // If we can't add any more, return the current items unchanged
        if (quantityToAdd <= 0) return currentItems;
        
        return currentItems.map(item =>
          item.id === itemToAdd.id
            ? { ...item, quantity: item.quantity + quantityToAdd }
            : item
        );
      }
      return [...currentItems, { ...itemToAdd, quantity: safeQuantity }];
    });
  };

  const removeItem = (id: string) => {
    setItems(currentItems => currentItems.filter(item => item.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) return;
    
    setItems(currentItems => {
      const item = currentItems.find(item => item.id === id);
      if (!item) return currentItems;
      
      // Enforce stock limit
      const safeQuantity = item.stock !== undefined 
        ? Math.min(quantity, item.stock)
        : quantity;
        
      return currentItems.map(item =>
        item.id === id ? { ...item, quantity: safeQuantity } : item
      );
    });
  };

  const clearCart = () => {
    setItems([]);
    setAppliedPromocode(null);
    setPromocodeValidationMessage('');
  };

  return (
    <CartContext.Provider value={{ 
      items, 
      addItem, 
      removeItem, 
      updateQuantity, 
      clearCart,
      appliedPromocode,
      setAppliedPromocode,
      promocodeValidationMessage,
      setPromocodeValidationMessage
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}